import type { TrackedTranslation, TranslationTag } from '@ps/web-core-intl';
import { Size } from '@ps/web-core-store';
import { StarsButton, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useContext, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import ButtonGroup from '@swf/components/ButtonGroup/ButtonGroup';
import type { ModalContextValue } from '@swf/components/StarsModal/Context';
import { ModalContext } from '@swf/components/StarsModal/Context';
import Body from '@swf/components/StarsModal/components/Body';
import type { Dispatch } from '@swf/core/store';
import { BaseHeader, SaGLogo, useWidgetTheme } from '@swf/poker';

import { TXTMOB_Skip } from './OnboardingTour.messages.json';
import styles from './OnboardingTour.scss';

interface Props {
  heading: TranslationTag;
  text: TranslationTag;
  primaryButtonText: TrackedTranslation;
  hideSecondaryButton?: boolean;
}

const OnboardingTour: React.FC<Props> = ({
  heading,
  text,
  primaryButtonText,
  hideSecondaryButton = false
}) => {
  const { localSettings, modal } = useDispatch<Dispatch>();
  const { className } = useWidgetTheme();
  const { setStyle } = useContext<ModalContextValue>(ModalContext);

  const primaryButtonClickedRef = useRef(false);

  useEffect(
    () => () => {
      if (!primaryButtonClickedRef.current) {
        localSettings.set({
          poker: { spinAndGo: { onboardingFinished: true, onboardingModalShown: true } }
        });
      }
    },
    [localSettings]
  );

  useEffect(() => {
    setStyle({
      closeOnOverlayClick: true,
      closeOnEsc: true,
      overlayClassName: classNames(className, styles.modalOverlay),
      className: classNames(styles.modalContainer, styles.modalContainerCentered),
      transparentBackground: true,
      size: Size.MEDIUM
    });
  }, [className, setStyle]);

  return (
    <>
      <BaseHeader headerClassName={styles.header} />
      <Body className={styles.body}>
        <SaGLogo />
        <h3 className={styles.heading}>
          <Translation {...heading} />
        </h3>
        <Translation {...text} />
        <ButtonGroup className={styles.buttons} inline>
          <StarsButton
            variant="primary"
            onClick={() => {
              primaryButtonClickedRef.current = true;
              localSettings.set({ poker: { spinAndGo: { onboardingModalShown: true } } });
              modal.close();
            }}
            translation={primaryButtonText}
          />
          {!hideSecondaryButton && (
            <StarsButton
              variant="secondary"
              onClick={() => modal.close()}
              translation={TXTMOB_Skip}
              className={styles.skip}
            />
          )}
        </ButtonGroup>
      </Body>
    </>
  );
};

export default OnboardingTour;
